/**
 * Utility functions for appending details to completed interactions
 *
 * Business Rule: Users can append additional details to completed interactions
 * within 7 days of the completion date, maintaining an audit trail.
 */

/**
 * Check if a completed interaction is within the 7-day append window
 * @param completedDate - ISO date string when interaction was completed
 * @returns true if within 7-day window, false otherwise
 */
export function isWithinAppendWindow(completedDate: string | undefined): boolean {
  if (!completedDate) return false;

  const completed = new Date(completedDate);
  const now = new Date();

  // Calculate difference in days
  const diffTime = now.getTime() - completed.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  // Within 7 days (inclusive)
  return diffDays >= 0 && diffDays <= 7;
}

/**
 * Get remaining days in the append window
 * @param completedDate - ISO date string when interaction was completed
 * @returns number of days remaining, or 0 if window expired
 */
export function getRemainingAppendDays(completedDate: string | undefined): number {
  if (!completedDate) return 0;

  const completed = new Date(completedDate);
  const now = new Date();

  const diffTime = now.getTime() - completed.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  const remaining = 7 - diffDays;
  return remaining > 0 ? remaining : 0;
}

/**
 * Format appended content with markdown separator and timestamp
 * @param existingContent - Current keterangan content
 * @param newContent - New content to append
 * @param currentDate - Current date (defaults to now)
 * @returns Formatted content with separator
 */
export function formatAppendedContent(
  existingContent: string,
  newContent: string,
  currentDate?: string
): string {
  const timestamp = currentDate || new Date().toISOString();

  // Format as ISO date-time: YYYY-MM-DD HH:mm:ss
  const date = new Date(timestamp);
  const formattedDate = date.toISOString().replace('T', ' ').substring(0, 19);

  // Use equals separator with ISO timestamp
  const separator = `\n\n========== ${formattedDate} ==========\n\n`;

  return `${existingContent}${separator}${newContent}`;
}

/**
 * Validate if content can be appended (not empty, within character limit)
 * @param content - Content to append
 * @param maxLength - Maximum allowed length (default 5000 chars)
 * @returns validation result with error message if invalid
 */
export function validateAppendContent(
  content: string,
  maxLength: number = 5000
): { valid: boolean; error?: string } {
  if (!content || content.trim().length === 0) {
    return { valid: false, error: 'Content cannot be empty' };
  }

  if (content.length > maxLength) {
    return {
      valid: false,
      error: `Content exceeds maximum length of ${maxLength} characters`
    };
  }

  return { valid: true };
}
